import React, { useState } from 'react';
import { Box, TextField, Typography, Button } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomAppBar from '../components/CustomAppBar';
import Navbar from '../components/Navbar';
import PrimaryButton from '../components/PrimaryButton';
import AppContainer from '../components/AppContainer';
import TextContainer from '../components/TextContainer';
import CloseIcon from '@mui/icons-material/Close';
// redux
import { useDispatch, useSelector } from 'react-redux';
import { updateTitle, updateSubtitle } from '../redux/doneSlice';
import { addGroup, addGroupToReduxAndServer, updateGroupOnReduxAndServer } from '../redux/groupSlice';
import { updateHeartOnReduxAndServer } from '../redux/heartSlice';
import { addUserToReduxAndServer } from '../redux/userSlice';
// api
import { fetchAndDispatchGroupHearts } from '../api/apiFetcher';
// yup / formik validation
import { groupIDSchema } from '../validation/schemas';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { queryGroupFromServer, queryServer } from '../api/api';
// schemas
import { GroupSchema, HeartSchema, UserSchema } from 'lumas-shared-schemas';
// snackbar
import { useSnackbar } from 'notistack';

function JoinGroup () {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [searchParams] = useSearchParams();
    const heartId = searchParams.get('heartId');

    const user = useSelector((state) => state.user.user);
    const groups = useSelector((state) => state.group.groups);
    const hearts = useSelector((state) => state.heart.hearts);
    const groupHearts = useSelector((state) => state.heart.groupHearts);

    const handleFormSubmit = (values, {setSubmitting, setFieldError}) => {
        // TODO: add a spinner while waiting for the response from the server?
        
        queryServer(values.groupID, 'groups').then((response) => {
            console.log("Response: ", response);
            const newGroupID = response.id;
                
            // Prevent joining if user is already a member of the group
            const userAlreadyInGroup = (user && user.groups && user.groups.includes(response.id)) ||
                (response && response.members && response.members.some(m => m.email === user.email));
                // some() stops if function returns true for any element -> efficient way to check if user in group
                // includes does a generic equality check, which works for primitive types like strings (group IDs in user's groups array), but not for objects (members in group's members array), hence the need for some() with email comparison for members array

            if (userAlreadyInGroup) {
                enqueueSnackbar(`You are already a member of this group.`, { variant: 'error' });
                setSubmitting(false);
                return;
            }
            // Add group ID to the User's groups array, validating against schema in lumas-shared-schemas
            const updatedUser = UserSchema.parse({
                ...user,
                groups: [...user.groups, response.id]
            });
            console.log("User: ", user, "Updated User: ", updatedUser);
            
            // Add User ID to the group's members array as a non-admin, validating against schema in lumas-shared-schemas
            let updatedGroup = GroupSchema.parse({
                ...response,
                members: [...response.members, {email: user.email, admin: false}]
            });

            // If a `heartId` was provided via query params, associate the heart with this group locally
            // and leave placeholders where server persistence calls would go.
            if (heartId) {
                // Find the heart in the local store
                const heartToUpdate = hearts.find(h => h.id === heartId);
                if (heartToUpdate) {
                    // Create a new group object that includes the heart id
                    updatedGroup = GroupSchema.parse({
                        ...updatedGroup,
                        hearts: [...(updatedGroup.hearts || []), heartId]
                    });

                    // Update the heart's group reference
                    const updatedHeart = HeartSchema.parse({ 
                        ...heartToUpdate, 
                        group: newGroupID
                    });
                    dispatch(updateHeartOnReduxAndServer({ id: heartId, updatedHeart }));
                } else {
                    console.warn('Heart with id', heartId, 'not found in local store.');
                }
            }
            
            // Dispatch AFTER schema validations to prevent creation of out-of-sync database values during development
            dispatch(addGroup(response));   // Add the group to the store (local only)
            dispatch(addUserToReduxAndServer(updatedUser));
            dispatch(updateGroupOnReduxAndServer({id: response.id, updatedGroup: updatedGroup}));

            // add groupHearts from new group in the store
            fetchAndDispatchGroupHearts([updatedGroup], dispatch, hearts, groupHearts);

            // Update the done title and subtitle messages in the store
            dispatch(updateTitle(`${values.groupID} joined succesfully!`));
            dispatch(updateSubtitle("You are now a member of the group. The other members of the group will now see the changes in color you make to the heart."));


            navigate(`/join-group/done`);
        }).catch((error) => {
            // Group not found, show error message
            if (error.response && error.response.status === 404) {
                setFieldError('groupID', 'Group not found');
            } else {
                console.error("Error fetching group: ", error);
            }
        });
        
        setSubmitting(false); // Set submitting to false after delay

    }

    return (
        <AppContainer>
            <Formik
                initialValues={{ groupID: '' }}
                validationSchema={groupIDSchema}
                onSubmit={handleFormSubmit}
            >
                    {({ isSubmitting, errors, touched }) => (
                        <Form>
                            {/* Box to fill space, pushing button to bottom of screen */}
                            <Box style={{ display: 'flex', flexDirection: 'column', minHeight: window.innerHeight}}>
                                <Box style={{ flex: 1 }}>
                                <CustomAppBar />
                                <Navbar rightButtonIcon={<CloseIcon />} rightButtonOnClick={() => {navigate('/home')}} />
                                        <TextContainer>
                                            <Typography variant='h5'>Join by Invite Code</Typography>
                                            <Typography variant='body2' fontStyle={"italic"}>Enter the invite code to join the group.</Typography>
                                            <Field 
                                                as={TextField}
                                                id='groupID'
                                                name='groupID'
                                                sx={{width: '100%'}} 
                                                label="Enter Group ID" 
                                                variant="filled" 
                                                error={errors.groupID && touched.groupID}
                                            />
                                            <ErrorMessage name="groupID" component="div" style={{ color: 'red' }} />
                                        </TextContainer>
                                    
                                </Box>
                                {/* Button at the bottom of the screen */}
                                <TextContainer customStyles={{marginBottom: '40px'}}>
                                    <Button 
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                        sx={{width: '100%'}}
                                    >
                                        Join Group
                                    </Button>
                                    {/* <PrimaryButton text={"Join Group"} customStyles={{width: '100%'}} onClick={handleJoinClick}/> */}
                                </TextContainer>
                            </Box>
                        </Form>
                )}
                </Formik>
        </AppContainer>
    )
}

export default JoinGroup